import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import { adaptAnswers } from 'adaptors/adaptAnswers';
import { useSetChosenRisk } from 'hooks/useSetChosenRisk';
import useFormatting from 'locale/useFormatting';
import { useClientProfile } from 'prodivers/clientProfile';
import { getClientId } from 'utils';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { formDataSelector, questionnaireSelector } from '../selectors';
import { useSelector } from '../useSelector';

const QUESTION_IDS = {
    age: 226,
    annualGrossIncome: 228,
    annualSavings: 229,
    additionalAssets: 230,
    reservedLiquidity: 231,
    assetClass: 232,
    sharpDropInPrice: 233,
    futureOfEconomicDevelopment: 234,
    investmentExperience: 235,
} as const;

interface InvestorProfileProps {
    dfsClientId: string | number;
    clientAge?: string | number;
    advisoryDataSelector?: any; // Replace with specific type if available
}

interface Question {
    Id: number;
    Answers: Array<{ Id: string | number }>;
    Value?: { Ids: any[] };
}

export const useInvestorProfile = ({
    dfsClientId,
    clientAge,
    advisoryDataSelector = null,
}: InvestorProfileProps) => {
    const {
        t,
    } = useTranslation();
    const { getFormattedDate } = useFormatting();

    const clientId = getClientId(dfsClientId as string) as number;
    const [isEditMode, setEditMode] = useState<boolean>(true);
    const logicInitialized = useRef(false);

    const showInvestorProfileEditView = useCallback(() => { setEditMode(true); }, []);
    const hideInvestorProfileEditView = useCallback(() => { setEditMode(false); }, []);

    const annualGrossIncome = advisoryDataSelector?.metadata?.incomeLiquidity?.grossAnnualIncome;
    const annualSavings = advisoryDataSelector?.metadata?.incomeLiquidity?.annualSavedAmount;
    const additionalAssets = advisoryDataSelector?.metadata?.incomeLiquidity?.totalLiquidity;
    const calculatedInvestmentAmount = advisoryDataSelector?.metadata?.calculatedInvestmentAmount;

    const totalAmountOfOtherAssets = Number(sessionStorage.getItem('totalAmountOfOtherAssets'));

    const sumOfLiquidityAndOtherAssets = useMemo(
        () => (calculatedInvestmentAmount || 0) + (totalAmountOfOtherAssets || 0),
        [calculatedInvestmentAmount, totalAmountOfOtherAssets],
    );

    const reservedLiquidityOf3MonthsSalary = useMemo(() => {
        const threeMonthsSalary = (annualGrossIncome / 4);

        if (additionalAssets && threeMonthsSalary) {
            return (additionalAssets || 0) >= threeMonthsSalary ? 'true' : 'false';
        }

        return undefined;
    }, [advisoryDataSelector?.metadata?.incomeLiquidity, annualGrossIncome, additionalAssets]);

    // Client Provider
    const information = useClientProfile();

    const {
        data,
        fullData,
        isLoading,
        validateQuestionnaire,
        isCompletedAndNotExpired,
        isLoadingPost,
        postQuestionnaire,
        calculateRisk,
        error: pageError,
    } = useSelector(questionnaireSelector);

    const {
        setChosenRisk,
        isSending: isChosenRiskSending,
        error: chosenRiskError,
    } = useSetChosenRisk({
        clientId,
        withAccept: true,
    });

    const {
        data: externalInvestorProfileDataFromZKP,
        isLoading: externalInvestorProfileDataFromZKPLoading,
        error: externalInvestorProfileDataFromZKPError,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getExternalInvestorProfileDataFromZKP',
        payload: {
            contactId: clientId,
        },
        enabled: !!clientId,
    });

    const {
        mutateAsync: postExternalInvestorProfile,
    } = useGenericAPIMutation();

    const investorProfileGroup = useMemo(() => data?.Groups?.[0], [data?.Groups]);
    const groupId = useMemo(() => investorProfileGroup?.Id, [investorProfileGroup?.Id]);

    const getQuestionByQuestionId = useCallback(({ questionId }: { questionId: number }) => investorProfileGroup?.Steps?.[0].Questions?.find((question: Question) => question?.Id === questionId), [investorProfileGroup?.Steps]);

    const getExternalAnswersByQuestionId = useCallback(({ questionId }: { questionId: number }) => externalInvestorProfileDataFromZKP?.data?.questionAnswers?.find((answer: any) => answer?.questionId === questionId)?.answerIds, [externalInvestorProfileDataFromZKP?.data?.questionAnswers]);

    const ageQuestion = useMemo(() => getQuestionByQuestionId({ questionId: QUESTION_IDS.age }), [getQuestionByQuestionId]);
    const grossIncomeQuestion = useMemo(() => getQuestionByQuestionId({ questionId: QUESTION_IDS.annualGrossIncome }), [getQuestionByQuestionId]);
    const annualSavingsQuestion = useMemo(() => getQuestionByQuestionId({ questionId: QUESTION_IDS.annualSavings }), [getQuestionByQuestionId]);
    const additionalAssetsQuestion = useMemo(() => getQuestionByQuestionId({ questionId: QUESTION_IDS.additionalAssets }), [getQuestionByQuestionId]);
    const reservedLiquidityOf3MonthsSalaryQuestion = useMemo(() => getQuestionByQuestionId({ questionId: QUESTION_IDS.reservedLiquidity }), [getQuestionByQuestionId]);

    const isQuestionnaireCompletedAndNotExpired = useMemo(() => isCompletedAndNotExpired(fullData), [fullData?.ExpiryDate, fullData?.IsComplete, isCompletedAndNotExpired]);
    const expirationDate = useMemo(() => getFormattedDate(fullData?.ExpiryDate), [fullData?.ExpiryDate, getFormattedDate]);

    const isZKPDataNew = useMemo(() => {
        const submittedOn = externalInvestorProfileDataFromZKP?.data?.submittedOn;
        const submissionDate = fullData?.SubmissionDate;

        if (!submittedOn) {
            return false;
        } if (submittedOn && !submissionDate) {
            return true;
        }

        return (new Date(submittedOn).getTime()) - (new Date(submissionDate).getTime()) >= 0;
    }, [externalInvestorProfileDataFromZKP?.data?.submittedOn, fullData?.SubmissionDate]);

    const {
        data: formData,
        initializeData,
        saveData,
    } = useSelector(formDataSelector);

    const {
        isValid: allQuestionsFilled,
        errors,
    } = useMemo(
        () => validateQuestionnaire({
            groupId,
            answers: formData,
        }),
        [formData, groupId, validateQuestionnaire],
    );

    useEffect(() => {
        if (isQuestionnaireCompletedAndNotExpired && !isZKPDataNew) {
            hideInvestorProfileEditView();
        }
    }, [isQuestionnaireCompletedAndNotExpired, isZKPDataNew, hideInvestorProfileEditView]);

    useEffect(() => {
        if (!formData && data?.Groups?.length) {
            const groupIds = data.Groups.map(({ Id }: { Id: string | number }) => Id);

            initializeData({ groupIds });
        }
    }, [data, formData, initializeData]);

    useEffect(() => {
        if (data && Object.keys(data).length && groupId && !logicInitialized?.current) {
            let questions: Array<{ questionId: number | undefined; value: any }> = [];

            // Age Logic
            if (clientAge) {
                let answerIndex = 0;
                const age = parseInt(clientAge as string, 10);

                if (age < 35) answerIndex = 0;
                else if (age < 51) answerIndex = 1;
                else if (age < 61) answerIndex = 2;
                else answerIndex = 3;

                questions.push({
                    questionId: ageQuestion?.Id,
                    value: ageQuestion?.Answers[answerIndex].Id,
                });
            } else if (isZKPDataNew) {
                questions.push({
                    questionId: ageQuestion?.Id,
                    value: getExternalAnswersByQuestionId({ questionId: ageQuestion?.Id as number })?.[0],
                });
            }

            // Income Logic
            if (annualGrossIncome) {
                let answerIndex = 0;
                const income = parseInt(annualGrossIncome as string, 10);

                if (income < 75000) answerIndex = 0;
                else if (income <= 150000) answerIndex = 1;
                else if (income <= 300000) answerIndex = 2;
                else if (income <= 500000) answerIndex = 3;
                else answerIndex = 4;

                questions.push({
                    questionId: grossIncomeQuestion?.Id,
                    value: grossIncomeQuestion?.Answers[answerIndex].Id,
                });
            } else if (isZKPDataNew) {
                questions.push({
                    questionId: grossIncomeQuestion?.Id,
                    value: getExternalAnswersByQuestionId({ questionId: grossIncomeQuestion?.Id as number })?.[0],
                });
            }

            // Savings Logic
            if (annualSavings) {
                const answerIndex = parseInt(annualSavings as string, 10) < 10000 ? 0 : 1;

                questions.push({
                    questionId: annualSavingsQuestion?.Id,
                    value: annualSavingsQuestion?.Answers[answerIndex].Id,
                });
            } else if (isZKPDataNew) {
                questions.push({
                    questionId: annualSavingsQuestion?.Id,
                    value: getExternalAnswersByQuestionId({ questionId: annualSavingsQuestion?.Id as number })?.[0],
                });
            }

            // Assets Logic
            if (sumOfLiquidityAndOtherAssets) {
                let answerIndex = 0;
                const totalAssets = parseInt(sumOfLiquidityAndOtherAssets.toString(), 10);

                if (totalAssets < 150000) answerIndex = 0;
                else if (totalAssets <= 350000) answerIndex = 1;
                else if (totalAssets <= 650000) answerIndex = 2;
                else if (totalAssets <= 1000000) answerIndex = 3;
                else if (totalAssets <= 2000000) answerIndex = 4;
                else answerIndex = 5;

                questions.push({
                    questionId: additionalAssetsQuestion?.Id,
                    value: additionalAssetsQuestion?.Answers[answerIndex].Id,
                });
            } else if (isZKPDataNew) {
                questions.push({
                    questionId: additionalAssetsQuestion?.Id,
                    value: getExternalAnswersByQuestionId({ questionId: additionalAssetsQuestion?.Id as number })?.[0],
                });
            }

            // Liquidity Logic
            if (reservedLiquidityOf3MonthsSalary) {
                const answerIndex = reservedLiquidityOf3MonthsSalary === 'true' ? 0 : 1;

                questions.push({
                    questionId: reservedLiquidityOf3MonthsSalaryQuestion?.Id,
                    value: reservedLiquidityOf3MonthsSalaryQuestion?.Answers[answerIndex].Id,
                });
            } else if (isZKPDataNew) {
                questions.push({
                    questionId: reservedLiquidityOf3MonthsSalaryQuestion?.Id,
                    value: getExternalAnswersByQuestionId({ questionId: reservedLiquidityOf3MonthsSalaryQuestion?.Id as number })?.[0],
                });
            }

            if (isZKPDataNew) {
                questions = [
                    ...questions,
                    {
                        questionId: QUESTION_IDS.assetClass,
                        value: getExternalAnswersByQuestionId({ questionId: QUESTION_IDS.assetClass })?.[0],
                    },
                    {
                        questionId: QUESTION_IDS.sharpDropInPrice,
                        value: getExternalAnswersByQuestionId({ questionId: QUESTION_IDS.sharpDropInPrice })?.[0],
                    },
                    {
                        questionId: QUESTION_IDS.futureOfEconomicDevelopment,
                        value: getExternalAnswersByQuestionId({ questionId: QUESTION_IDS.futureOfEconomicDevelopment })?.[0],
                    },
                    {
                        questionId: QUESTION_IDS.investmentExperience,
                        value: getExternalAnswersByQuestionId({ questionId: QUESTION_IDS.investmentExperience }),
                    },
                ];
            }

            if (questions?.length) {
                saveData({
                    groupId,
                    questions,
                });
                logicInitialized.current = true;
            }
        }
    }, [
        clientAge,
        annualGrossIncome,
        annualSavings,
        additionalAssetsQuestion,
        reservedLiquidityOf3MonthsSalary,
        data,
        groupId,
        ageQuestion,
        grossIncomeQuestion,
        annualSavingsQuestion,
        reservedLiquidityOf3MonthsSalaryQuestion,
        sumOfLiquidityAndOtherAssets,
        isZKPDataNew,
        getExternalAnswersByQuestionId,
        saveData,
        logicInitialized?.current,
    ]);

    const onChange = useCallback(({
        questionId,
        value,
    }: { questionId: number; value: any }) => {
        saveData({
            groupId,
            questions: [{
                questionId,
                value,
            }],
        });
    }, [groupId, saveData]);

    const isDataChanged = useCallback(() => {
        const answers = adaptAnswers({
            data,
            values: formData,
            groupId,
        });

        if (answers?.Groups?.[0] && data?.Groups?.[0]) {
            return !isEqual(answers?.Groups?.[0], data?.Groups?.[0]);
        }

        return false;
    }, [data, groupId, formData]);

    const handlePostExternalQuestionnaire = useCallback(async () => {
        await postExternalInvestorProfile({
            service: 'customClientService',
            API: 'postExternalInvestorProfileToZKP',
            payload: { contactId: clientId },
        });
    }, [clientId, postExternalInvestorProfile]);

    return {
        isEditMode,
        isDataChanged,
        isQuestionnaireCompletedAndNotExpired,
        expirationDate,
        showInvestorProfileEditView,
        hideInvestorProfileEditView,
        isLoading: information?.isLoading || isLoading || isLoadingPost || isChosenRiskSending || externalInvestorProfileDataFromZKPLoading,
        clientId,
        postQuestionnaire,
        handlePostExternalQuestionnaire,
        calculateRisk,
        setChosenRisk,
        allQuestionsFilled,
        errors,
        investorProfileGroup,
        formData,
        groupId,
        data,
        onChange,
        pageError: pageError || chosenRiskError || externalInvestorProfileDataFromZKPError,
        t,
        score: fullData?.Score,
        fullData,
    };
};
